import { Modal } from 'antd';
import { useState, useEffect } from 'react';
import { TCustomer, TSmartVariableDefinition } from '@/types/TSmartCRM';
import { ECustomerModalType, EFieldType } from 'src/enums/ESmartCRM';
import { CSVUploadModal } from './CSVUploadModal';
import { CustomerForm } from './CustomerForm';
import { CustomerTypeSelection } from './TypeSelection';
import { showErrorNotification } from '@/utils/SmartCRM/fieldUtils';

interface CustomerModalProps {
	visible: boolean;
	onCancel: () => void;
	onSave: (customerData: Omit<TCustomer, 'id'>) => Promise<void>;
	onBulkSave?: (customers: Omit<TCustomer, 'id'>[]) => Promise<void>;
	initialData?: TCustomer | null;
	agentId?: string;
	variables?: TSmartVariableDefinition[];
}

export const CustomerModal = ({
	visible,
	onCancel,
	onSave,
	onBulkSave,
	initialData,
	agentId,
	variables = [],
}: CustomerModalProps) => {
	const [modalType, setModalType] = useState<ECustomerModalType | null>(null);
	const [csvModalVisible, setCsvModalVisible] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const isEditing = Boolean(initialData);

	useEffect(() => {
		if (!visible) {
			setModalType(null);
			setCsvModalVisible(false);
		}
	}, [visible]);

	const handleTypeSelect = (type: ECustomerModalType): void => {
		if (type === ECustomerModalType.CSV_UPLOAD) {
			setCsvModalVisible(true);
		} else {
			setModalType(type);
		}
	};

	const handleCSVSubmit = async (csvData: any[]) => {
		if (!csvData || !Array.isArray(csvData) || csvData.length === 0) {
			showErrorNotification('No data to import', 'The CSV file appears to be empty or invalid');
			return;
		}

		setLoading(true);
		try {
			const customers = csvData?.map((row) => {
				const variablesData: { [key: string]: any } = {};

				variables.forEach((v) => {
					const value = row[v.internalKey];
					if (value !== undefined && value !== null && value !== '') {
						let processedValue = value;
						if (v.fieldType === EFieldType.DATE && value) {
							processedValue = value.toDate ? value.toDate() : new Date(value);
						}
						variablesData[v.id] = processedValue;
					}
				});

				return {
					name: row.name,
					email: row.email,
					number: row.number,
					variables: variablesData,
				};
			});

			await onBulkSave?.(customers);
			setCsvModalVisible(false);
			onCancel();
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async (customerData: Omit<TCustomer, 'id'>) => {
		setLoading(true);
		try {
			await onSave(customerData);
			onCancel();
		} finally {
			setLoading(false);
		}
	};

	const handleBack = (): void => {
		setModalType(null);
	};

	const handleCancel = (): void => {
		if (csvModalVisible) {
			setCsvModalVisible(false);
		} else {
			setModalType(null);
			onCancel();
		}
	};

	const handleCSVCancel = (): void => {
		setCsvModalVisible(false);
	};

	const getModalContent = () => {
		if (csvModalVisible) {
			return (
				<CSVUploadModal
					visible={true}
					onCancel={handleCSVCancel}
					onSubmit={handleCSVSubmit}
					variables={variables}
				/>
			);
		}

		if (isEditing) {
			return (
				<CustomerForm
					onSave={handleSave}
					onCancel={handleCancel}
					initialData={initialData}
					agentId={agentId}
					variables={variables}
					isEditing={isEditing}
					loading={loading}
				/>
			);
		}

		switch (modalType) {
			case ECustomerModalType.MANUAL:
				return (
					<CustomerForm
						onSave={handleSave}
						onCancel={handleCancel}
						onBack={handleBack}
						agentId={agentId}
						variables={variables}
						isEditing={isEditing}
						loading={loading}
					/>
				);
			default:
				return <CustomerTypeSelection onTypeSelect={handleTypeSelect} />;
		}
	};

	const isShowingForm = isEditing || modalType === ECustomerModalType.MANUAL;
	const shouldShowModal = visible && !csvModalVisible;

	return (
		<>
			<Modal
				title={null}
				open={shouldShowModal}
				onCancel={handleCancel}
				footer={null}
				width={1000}
				centered
				destroyOnClose
				style={{
					height: isShowingForm ? '80vh' : 'auto',
				}}
				styles={{
					body: {
						padding: 0,
						height: isShowingForm ? '80vh' : 'auto',
						overflow: 'hidden',
					},
				}}
			>
				{getModalContent()}
			</Modal>

			<CSVUploadModal
				visible={csvModalVisible}
				onCancel={handleCSVCancel}
				onSubmit={handleCSVSubmit}
				variables={variables}
			/>
		</>
	);
};
